import type { SensorEvent } from "@/base/sensor-events"
import type { BadgeProps } from "@/theme/Badge"

import React from "react"
import { useTranslation } from "react-i18next"

import { AppText, Box, COLORS, Row } from "@/theme"
import { useIsDarkMode } from "@/theme/AppThemeContextProvider"
import { Badge } from "@/theme/Badge"
import { AppIcons } from "@/theme/icons/AppIcons"

import { ProgressBar } from "./RunProgressBar"

import { StyleSheet } from "react-native"
import { getSensorUnitLabel } from "@/base/getSensorUnitLabel"
import { useRootSelector } from "@/redux/useRootSelector"
import { getUserMeasurementPreferenceFromState } from "@/redux/selectors"
import { useConvertSensorValue } from "@/base/sensor-conversions"

export type VfdIndicatorProps = Pick<
  SensorEvent<"vfd">,
  | "currentPercentage"
  | "pidSensorSetpoint"
  | "stateCurrent"
  | "targetRampPercentage"
  | "pidMeasurementType"
>

/**
 * Display VFD
 */
export function VfdIndicatorLarge({
  currentPercentage,
  pidMeasurementType,
  pidSensorSetpoint,
  stateCurrent,
  targetRampPercentage,
}: VfdIndicatorProps) {
  const { t } = useTranslation("devices")

  let fill = 0

  let statusElement: React.JSX.Element = (
    <Badge StartIconComponent={"Timeline"}>{t("vfd.off")}</Badge>
  )

  switch (stateCurrent) {
    case null:
    case undefined:
    case "NONE":
    case "VFDOF": {
      break
    }
    case "VFDOV":
    case "VFDUN":
    case "VFDPIDT":
    case "VFDST": {
      let stateText = t("vfd.on")
      let badgeColorScheme: BadgeProps["colorScheme"] = "$green"
      if (stateCurrent === "VFDOV") {
        stateText = t("vfd.over")
        badgeColorScheme = "$warning"
      } else if (stateCurrent === "VFDUN") {
        stateText = t("vfd.under")
        badgeColorScheme = "$warning"
      }
      statusElement = (
        <Badge
          StartIconComponent={"Timeline"}
          colorScheme={badgeColorScheme}
          fontSize={"$sm"}
        >
          {stateText}
        </Badge>
      )

      if (typeof currentPercentage === "number") {
        fill = currentPercentage
      }
      break
    }

    case "VFDRP": {
      const isTargetValid = typeof targetRampPercentage === "number"
      if (isTargetValid) {
        fill = targetRampPercentage
      }
      const isCurrentValid = typeof currentPercentage === "number"
      if (isCurrentValid && isTargetValid) {
        let IconComponent = AppIcons.ArrowUp
        if (targetRampPercentage < currentPercentage) {
          IconComponent = AppIcons.ArrowDown
        }
        statusElement = (
          <Badge
            EndIconComponent={IconComponent}
            StartIconComponent={"Timeline"}
            colorScheme={"$green"}
          >
            {t("vfd.ramping")}
          </Badge>
        )
      }
      break
    }
  }

  const isDark = useIsDarkMode()
  const measurementPreference = useRootSelector(
    getUserMeasurementPreferenceFromState,
  )
  const convertFlow = useConvertSensorValue("rateLpmAvg");
  const convertPressure = useConvertSensorValue("pressureObservedKpa");

  let trackingText = null

  if(stateCurrent === "VFDPIDT" && typeof pidSensorSetpoint === "number"){
    let vfdTrackingUnits = ""
    let vfdTrackingValue = pidSensorSetpoint

    if(pidMeasurementType === 1){
      vfdTrackingUnits = getSensorUnitLabel({
        fieldName: "rateLpmAvg",
        labelLength: "short",
        measurementPreference
      })
      vfdTrackingValue = convertFlow(
        vfdTrackingValue,
        "user"
      )
    } else if(pidMeasurementType === 2){
      vfdTrackingUnits = getSensorUnitLabel({
        fieldName: "pressureObservedKpa",
        labelLength: "short",
        measurementPreference
      })
      vfdTrackingValue = convertPressure(
        vfdTrackingValue,
        "user"
      )
    }

    trackingText = (
      <AppText fontSize={"$sm"}>
        {t("vfd.trackingWithValue", {
          value: vfdTrackingValue,
          unit: vfdTrackingUnits
        })}
      </AppText>
    )
  }

  return (
    <Box
      bg={isDark ? COLORS.$gray[600] : COLORS.$gray[100]}
      mb={"$2"}
      px={"$2"}
      py={"$2"}
      rounded={"$default"}
      style={[styles.root, isDark && styles.rootDark]}
      w={"$64"}
    >
      <Row alignItems={"center"} justifyContent={"space-between"} pb={"$2"}>
        {statusElement}
        {trackingText}
        {/* This is the badge indicating the state of the VFD */}
      </Row>
      <ProgressBar
        barFillColor={COLORS.$green[300]}
        fillPercentage={fill}
        textLeft={`${currentPercentage ?? 0}%`}
      />
    </Box>
  )
}
const styles = StyleSheet.create({
  root: {
    borderColor: COLORS.$gray[300],
    borderRadius: 5,
    borderWidth: 2,
  },
  rootDark: {
    borderColor: COLORS.$gray[700],
  },
})
